import { useState } from "react";
import { Check, Ban, ShieldCheck } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import {
  useEmailDomainReview,
  useAllowEmailDomain,
  useBlockEmailDomain,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { cn } from "../../../../lib/utils";
import type {
  EmailDomainReviewItem,
  EmailDomainReviewList,
  EmailDomainReviewStatus,
} from "../../../../services/emailSettingsEndpoint";

const STATUS_TABS: Array<{ value: EmailDomainReviewStatus; label: string }> = [
  { value: "pending", label: "Pending" },
  { value: "allowed", label: "Allowed" },
  { value: "blocked", label: "Blocked" },
];

const verdictLabel = (verdict: string) => {
  if (verdict === "not_customer") return "Not a customer";
  if (verdict === "ambiguous") return "Ambiguous";
  return verdict;
};

const verdictClasses = (verdict: string) => {
  if (verdict === "not_customer") return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200";
  if (verdict === "ambiguous") return "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200";
  return "bg-muted text-muted-foreground";
};

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  if (status === "allowed") return "default";
  if (status === "blocked") return "destructive";
  return "secondary";
};

const DomainReviewPage = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;

  const [status, setStatus] = useState<EmailDomainReviewStatus>("pending");

  const reviewQuery = useEmailDomainReview({ workspaceId, status });
  const data = reviewQuery.data as EmailDomainReviewList | undefined;
  const isLoading = reviewQuery.isLoading;
  const allowMutation = useAllowEmailDomain();
  const blockMutation = useBlockEmailDomain();

  const items: EmailDomainReviewItem[] = data?.items ?? [];
  const pendingCount = data?.pending_count ?? 0;
  const sensitiveExcludedCount = data?.sensitive_excluded_count ?? 0;

  const isMutating = (domain: string) => (
    (allowMutation.isLoading && allowMutation.variables?.domain === domain)
    || (blockMutation.isLoading && blockMutation.variables?.domain === domain)
  );

  const handleAllow = (domain: string) => {
    if (!workspaceId) return;
    allowMutation.mutate({ workspace_id: workspaceId, domain });
  };

  const handleBlock = (domain: string) => {
    if (!workspaceId) return;
    blockMutation.mutate({ workspace_id: workspaceId, domain });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-medium text-foreground">Domain Review</h1>
            {pendingCount > 0 ? (
              <Badge variant="secondary" className="h-5 px-2 text-[11px]">
                {pendingCount} pending
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Review domains seen in inbound email and decide whether they should create accounts.
          </p>
        </div>
      </div>

      {sensitiveExcludedCount > 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span>
            <span className="font-medium text-foreground">{sensitiveExcludedCount}</span>{" "}
            message{sensitiveExcludedCount === 1 ? "" : "s"} excluded as sensitive and never ingested.
          </span>
        </div>
      ) : null}

      <Tabs value={status} onValueChange={(value) => setStatus(value as EmailDomainReviewStatus)}>
        <TabsList>
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/25 border-t-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-border py-10 text-center text-[13px] text-muted-foreground">
          No {status} domains.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full table-fixed">
            <colgroup>
              <col />
              <col className="w-[64px]" />
              <col className="w-[120px]" />
              <col className="w-[92px]" />
              <col className="w-[176px]" />
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5 text-left">Domain</th>
                <th className="px-2 py-2.5 text-center">Msgs</th>
                <th className="px-2 py-2.5 text-left">Verdict</th>
                <th className="px-2 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr key={item.id} className="transition-colors hover:bg-muted/40">
                  <td className="px-4 py-3">
                    <div className="truncate text-[13px] font-medium text-foreground" title={item.domain}>
                      {item.domain}
                    </div>
                    {item.reason ? (
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground" title={item.reason}>
                        {item.reason}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-2 py-3 text-center text-[13px] tabular-nums text-muted-foreground">
                    {item.msg_count}
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={cn(
                        "inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
                        verdictClasses(item.verdict),
                      )}
                    >
                      {verdictLabel(item.verdict)}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <Badge variant={statusVariant(item.status)} className="h-5 px-2 text-[11px] capitalize">
                      {item.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {item.status !== "allowed" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[12px]"
                          onClick={() => handleAllow(item.domain)}
                          disabled={isMutating(item.domain)}
                        >
                          <Check className="mr-1 h-3.5 w-3.5" /> Allow
                        </Button>
                      ) : null}
                      {item.status !== "blocked" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[12px] text-destructive hover:text-destructive"
                          onClick={() => handleBlock(item.domain)}
                          disabled={isMutating(item.domain)}
                        >
                          <Ban className="mr-1 h-3.5 w-3.5" /> Block
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DomainReviewPage;
